import { Fragment, type ReactNode } from 'react'
import { money, number, signedMoney } from '../lib/format'
import { billingContactLine, contractLengthMonths, licenseTermRows, discountRowLabel, isCreditLine, marketingRightsText, orderFormAttachments, orderFormCustomerSigner, orderFormTotals, recurringSuffix } from '../lib/order-form'
import { lineDiscountText, lineHasDiscount, lineIncluded, lineNetPrice, lineRepeats, linePriceText, linePricing, orderFormLineAmount, orderFormLineGrossAmount, periodsText } from '../lib/order-form-line'
import { parseTermsMarkdown, type TermsInline } from '../lib/terms-markdown'
import type { OrderForm, OrderFormLine, WorkspaceAsset } from '../types'

function longDate(value: string | null): string {
  if (!value) return '–'
  const parsed = Date.parse(value.length === 10 ? `${value}T12:00:00` : value)
  return Number.isNaN(parsed) ? value : new Date(parsed).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/** Per unit: the count, with the periods under it when the line repeats. Flat fee: "1", or the periods ("12 months"). */
function quantityCell(line: OrderFormLine, termMonths: number | null) {
  const periods = lineRepeats(line, termMonths)
  if (linePricing(line) === 'flat') return <span>{periods ? periodsText(periods) : '1'}</span>
  return <><span>{line.quantity === null ? '–' : number(line.quantity)}</span>{periods && <small className="order-form-line-note">× {periodsText(periods)}</small>}</>
}

/** The net price (or Included), then the normal price struck through and the discount. */
function priceCell(line: OrderFormLine) {
  const net = lineNetPrice(line)
  if (net === null || line.unitPrice === null) return <span>–</span>
  const credit = isCreditLine(line)
  if (!lineHasDiscount(line)) return <span className={credit ? 'order-form-credit' : undefined}>{linePriceText(line, net)}</span>
  const included = lineIncluded(line)
  return (
    <>
      <span>{included ? 'Included' : linePriceText(line, net)}</span>
      <small className="order-form-line-was"><s>{linePriceText(line, line.unitPrice)}</s>{!included && <span className="order-form-line-off">{lineDiscountText(line)}</span>}</small>
    </>
  )
}

function amountCell(line: OrderFormLine, termMonths: number | null) {
  const amount = orderFormLineAmount(line, termMonths)
  if (amount === null) return <span>–</span>
  const gross = lineHasDiscount(line) ? orderFormLineGrossAmount(line, termMonths) : null
  return <><span className={isCreditLine(line) ? 'order-form-credit' : undefined}>{signedMoney(amount)}</span>{gross !== null && gross > amount && <s>{money(gross)}</s>}</>
}

/** Styled runs as React elements. Links were checked by the parser (http, https or mailto only). */
function termsInline(children: TermsInline[]) {
  return children.map((child, index) => {
    let node: ReactNode = child.text
    if (child.italic) node = <em>{node}</em>
    if (child.bold) node = <strong>{node}</strong>
    if (child.href) node = <a href={child.href} target="_blank" rel="noopener noreferrer">{node}</a>
    return <Fragment key={index}>{node}</Fragment>
  })
}

/**
 * Additional Terms from the stored markdown: headings, paragraphs, bulleted
 * and numbered lists, bold, italic and links. Built as elements, never as HTML.
 */
export function AdditionalTerms({ source }: { source: string }) {
  return (
    <>
      {parseTermsMarkdown(source).map((block, index) => {
        if (block.type === 'heading') return block.level === 3 ? <h4 key={index}>{termsInline(block.children)}</h4> : <h3 key={index}>{termsInline(block.children)}</h3>
        if (block.type === 'paragraph') return <p key={index}>{termsInline(block.children)}</p>
        const items = block.items.map((item, position) => <li key={position}>{termsInline(item)}</li>)
        return block.ordered ? <ol key={index} start={block.start === 1 ? undefined : block.start}>{items}</ol> : <ul key={index}>{items}</ul>
      })}
    </>
  )
}

/**
 * The order form as a document, laid out like the Dock order forms customers
 * already know: header with both parties, a Licenses table with its own
 * discount (or, for a subscription discount, the discount in Totals), Add Ons
 * where a line discount shows the normal price struck through, Totals, Terms, additional
 * terms, signatures, then the agreements attached to it. Every total is
 * computed here from the stored lines so the document can never disagree.
 */
export function OrderFormDocument({ form, accountName, accountLogoUrl, assets }: { form: OrderForm; accountName: string; accountLogoUrl: string | null; assets: WorkspaceAsset[] }) {
  const totals = orderFormTotals(form)
  const licenses = form.lines.filter((line) => line.category === 'license')
  const addOns = form.lines.filter((line) => line.category === 'add_on')
  const oneTime = form.lines.filter((line) => line.category === 'one_time')
  const customerName = form.customer.legalName || accountName
  const suffix = recurringSuffix(form)
  const months = contractLengthMonths(form.termStart, form.termEnd)
  // The license block carries the discount only when it is a license discount.
  // A subscription discount (licenses and add-ons) shows in the Totals block.
  const licenseDiscount = totals.discountScope === 'licenses' && totals.discount > 0
  const subscriptionDiscount = totals.discountScope === 'subscription' && totals.discount > 0
  const discountText = discountRowLabel(form)
  const attachments = orderFormAttachments(form, assets)
  const totalLicenses = licenses.reduce((sum, line) => sum + (linePricing(line) === 'flat' ? 0 : line.quantity ?? 0), 0)
  const website = form.customer.website.replace(/^https?:\/\//, '')
  // Created by is the person who prepared the form; the Prentus signature block is the Prentus signer, who signs first.
  const creator = form.preparedBy?.email ? form.preparedBy : form.prentusContact
  const billing = billingContactLine(form.customer)

  return (
    <article className={`order-form-document status-${form.status}`} aria-label={`Order form ${form.number}`}>
      <header className="order-form-head">
        <div className="order-form-parties-logos">
          <img src="/prentus-logo-black.png" alt="Prentus" className="prentus-wordmark" />
          {accountLogoUrl && <img src={accountLogoUrl} alt={`${accountName} logo`} className="account-logo" />}
        </div>
        <h1>{form.title || `Prentus <> ${customerName}`}</h1>
        <div className="order-form-meta">
          <div>
            <h2>Company</h2>
            <p>{customerName}</p>
            {website && <p><a href={`https://${website}`} target="_blank" rel="noreferrer">{website}</a></p>}
            {form.customer.address && <p className="order-form-address">{form.customer.address}</p>}
          </div>
          <div>
            <h2>Business &amp; Billing Contact</h2>
            {form.customer.contactName && <p>{form.customer.contactName}</p>}
            {form.customer.contactTitle && <p>{form.customer.contactTitle}</p>}
            {form.customer.contactEmail && <p>{form.customer.contactEmail}</p>}
            {billing && <p><small>Billing</small>{billing}</p>}
          </div>
          <dl className="order-form-reference">
            <div><dt>Reference:</dt><dd>{form.number}</dd></div>
            <div><dt>Created:</dt><dd>{longDate(form.createdAt)}</dd></div>
            {form.validUntil && form.status !== 'signed' && <div><dt>Expires:</dt><dd>{longDate(form.validUntil)}</dd></div>}
            {form.status === 'signed' && <div><dt>Signed:</dt><dd>{longDate(form.signedAt)}</dd></div>}
            {creator.name && <div><dt>Created by:</dt><dd>{creator.name}</dd></div>}
            {creator.title && <div><dt></dt><dd>{creator.title}</dd></div>}
            {creator.email && <div><dt></dt><dd>{creator.email}</dd></div>}
          </dl>
        </div>
      </header>

      {licenses.length > 0 && (
        <section className="order-form-block">
          <header><h2>Active Jobseeker Licenses</h2>{totalLicenses > 0 && <span>Total licenses: {number(totalLicenses)}</span>}</header>
          <table className="order-form-lines">
            <thead><tr><th scope="col">Product</th><th scope="col" className="num">Number of licenses</th><th scope="col" className="num">Price per license</th><th scope="col" className="num">Total</th></tr></thead>
            <tbody>
              {licenses.map((line) => (
                <tr key={line.id}>
                  <td><strong>{line.name || 'Licenses'}</strong>{line.description && <small>{line.description}</small>}</td>
                  <td className="num">{quantityCell(line, months)}</td>
                  <td className="num">{priceCell(line)}</td>
                  <td className="num">{amountCell(line, months)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="order-form-block-totals">
            {licenseDiscount && (
              <>
                <div><span>Subtotal</span><span>{money(totals.licenses)}</span></div>
                <div className="order-form-discount"><span>{discountText}</span><span>-{money(totals.discount)}</span></div>
              </>
            )}
            <div className="strong"><span>Licenses Total</span><span>{money(totals.licenseTotal)}</span></div>
          </div>
        </section>
      )}

      {addOns.length > 0 && (
        <section className="order-form-block">
          <header><h2>Add Ons</h2></header>
          <table className="order-form-lines">
            <thead><tr><th scope="col">Description</th><th scope="col" className="num">Quantity</th><th scope="col" className="num">Unit Price</th><th scope="col" className="num">Total</th></tr></thead>
            <tbody>
              {addOns.map((line) => (
                <tr key={line.id}>
                  <td><strong>{line.name || 'Add on'}</strong>{line.description && <small>{line.description}</small>}</td>
                  <td className="num">{quantityCell(line, months)}</td>
                  <td className="num">{priceCell(line)}</td>
                  <td className="num">{amountCell(line, months)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="order-form-block-totals"><div className="strong"><span>Add-Ons Total</span><span>{money(totals.addOns)}{suffix}</span></div></div>
        </section>
      )}

      {oneTime.length > 0 && (
        <section className="order-form-block">
          <header><h2>One-Time Fees</h2></header>
          <table className="order-form-lines">
            <thead><tr><th scope="col">Description</th><th scope="col" className="num">Quantity</th><th scope="col" className="num">Unit Price</th><th scope="col" className="num">Total</th></tr></thead>
            <tbody>
              {oneTime.map((line) => (
                <tr key={line.id}>
                  <td><strong>{line.name || 'Fee'}</strong>{line.description && <small>{line.description}</small>}</td>
                  <td className="num">{quantityCell(line, months)}</td>
                  <td className="num">{priceCell(line)}</td>
                  <td className="num">{amountCell(line, months)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="order-form-block-totals"><div className={totals.oneTime < 0 ? 'strong order-form-discount' : 'strong'}><span>One-Time Total</span><span>{signedMoney(totals.oneTime)}</span></div></div>
        </section>
      )}

      {form.lines.length === 0 && <p className="order-form-empty">No line items have been added yet.</p>}

      <section className="order-form-block order-form-grand">
        <header><h2>Totals</h2></header>
        <div className="order-form-block-totals">
          {subscriptionDiscount && (
            <>
              <div><span>Licenses</span><span>{money(totals.licenses)}</span></div>
              <div><span>Add-Ons</span><span>{money(totals.addOns)}</span></div>
              <div><span>Subscription Subtotal</span><span>{money(totals.subscriptionSubtotal)}</span></div>
              <div className="order-form-discount"><span>{discountText}</span><span>-{money(totals.discount)}</span></div>
            </>
          )}
          <div><span>Recurring Total</span><span>{money(totals.recurring)}{suffix}</span></div>
          {oneTime.length > 0 && <div className={totals.oneTime < 0 ? 'order-form-discount' : undefined}><span>One-Time Total</span><span>{signedMoney(totals.oneTime)}</span></div>}
          <div className="strong"><span>Total</span><span>{signedMoney(totals.total)}</span></div>
        </div>
      </section>

      <section className="order-form-terms-grid">
        <h2>Terms</h2>
        <dl>
          <div><dt>Contract Length:</dt><dd>{months !== null ? `${months} months` : '–'}</dd></div>
          <div><dt>Invoice Schedule:</dt><dd>{form.invoiceSchedule || '–'}</dd></div>
          <div><dt>Start Date:</dt><dd>{longDate(form.termStart)}</dd></div>
          <div><dt>Payment terms:</dt><dd>{form.paymentTerms || '–'}</dd></div>
          <div><dt>End Date:</dt><dd>{longDate(form.termEnd)}</dd></div>
          <div><dt>Payment methods:</dt><dd>{form.paymentMethods || '–'}</dd></div>
          <div><dt>Billing Start:</dt><dd>{form.billingStart || '–'}</dd></div>
          <div><dt>Marketing rights:</dt><dd>{marketingRightsText(form.marketingRights)}</dd></div>
          {licenseTermRows(form).map((row) => <div key={row.label}><dt>{`${row.label}:`}</dt><dd>{row.value}</dd></div>)}
        </dl>
      </section>

      {form.additionalTerms && (
        <section className="order-form-additional">
          <h2>Additional terms</h2>
          <AdditionalTerms source={form.additionalTerms} />
        </section>
      )}

      <section className="order-form-signatures" aria-label="Signatures">
        {[
          (() => { const customer = orderFormCustomerSigner(form.customer); return { party: customerName, signer: customer.name, title: customer.title, email: customer.email } })(),
          { party: 'Prentus, Inc.', signer: form.prentusContact.name, title: form.prentusContact.title, email: form.prentusContact.email },
        ].map((block) => (
          <div className="order-form-signature" key={block.party}>
            <h2>{block.party}</h2>
            <div className="signature-line">{form.status === 'signed' && form.signedAt && <small>Signed {longDate(form.signedAt)}</small>}</div>
            <div className="signature-meta">
              <span><small>Name</small>{block.signer || '\u00a0'}</span>
              <span><small>Title</small>{block.title || '\u00a0'}</span>
              <span><small>Date</small>{form.status === 'signed' && form.signedAt ? longDate(form.signedAt) : '\u00a0'}</span>
            </div>
            {block.email && <p className="order-form-signature-email">{block.email}</p>}
          </div>
        ))}
      </section>

      {attachments.length > 0 && (
        <section className="order-form-attachments">
          <h2>Agreements attached to this Order Form</h2>
          <p>This Order Form is governed by the {attachments.length === 1 ? 'agreement' : 'agreements'} below. If there is any inconsistency between them and this Order Form, the Order Form controls.</p>
          <ol>
            {attachments.map((attachment) => (
              <li key={attachment.id}>
                {attachment.url ? <a href={attachment.url} target="_blank" rel="noreferrer">{attachment.title}</a> : attachment.title}
                {attachment.kind === 'default_csa' && <small>Prentus standard</small>}
              </li>
            ))}
          </ol>
        </section>
      )}
    </article>
  )
}
